import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { TabContent, TabPane } from 'reactstrap';
import { useParams, useNavigate, generatePath } from 'react-router-dom';
import './DealDashboard.scss';

import { NSNavTabs, NSDropdown, NSButton } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { useAuthContext } from 'contexts/AuthContext';
import CreateProFormaModal from 'views/CreateProFormaModal/CreateProFormaModal';
import { useSlideout } from 'contexts/BackOfEnvelopeContext';
import RenderIf from 'components/RenderIf/RenderIf';
import PageTitle from 'components/PageTitle/PageTitle';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import useToast, { ToastType } from 'hooks/useToast';
import { downloadFileFromUrl } from 'helpers/file/download';
import { Deal } from 'views/Deals/types';
import { FEATURE_IDS } from 'services/constants';
import { AccessLevels } from 'types/auth';
import { URLS } from 'services/urls';
import {
    DEAL_OVERVIEW, DEAL_OVERVIEW_TABS, BACK_OF_ENVELOPE, DEAL_ACTIVITY, ExportOptions,
    TAB_TO_SLUG, SLUG_TO_TAB,
} from './constants';
import Overview from './Overview/Overview';
import BackOfEnvelope from './BOE/BackOfEnvelope';
import { getDealOverviewPdf } from './Overview/hooks/useGetDealOverviewPdf';
import { getScenarioDashboardPdf } from './Overview/hooks/useGetScenarioDashboardPdf';
import { useGetBackOfEnvelopeScenarios } from './BOE/hooks/useGetBOEScenarios';
import { PageSpecificKeys, useLocalStorage } from '../../helpers/localStorage';
import { ILocallyStoredActiveTabName } from './types';
import Activity from './Activity/Activity';

const DealDashboard = () => {
    const { dealId, tab: tabParam } = useParams<{ dealId: string; tab?: string }>();
    const navigate = useNavigate();
    const { selectedOrganizationId, isFeatureAccessibleToUser } = useAuthContext();
    const [isDownloading, setIsDownloading] = useState(false);
    const [openCreateProFormaModal, setOpenCreateProFormaModal] = useState(false);

    let visibleDealOverviewTabs = DEAL_OVERVIEW_TABS;

    const [locallyStoredActiveTabName, setLocallyStoredActiveTabName] = useLocalStorage<ILocallyStoredActiveTabName>(
        PageSpecificKeys.DealDashboardPage,
        {},
    );

    const activeTabName = useMemo(() => {
        if (tabParam && SLUG_TO_TAB[tabParam]) {
            return SLUG_TO_TAB[tabParam];
        }

        return locallyStoredActiveTabName[dealId as string]?.tab || DEAL_OVERVIEW;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabParam]);

    const defaultScenarioId = locallyStoredActiveTabName[dealId as string]?.scenarioId;
    const { showToast } = useToast();
    const { selectedScenarioId, selectedScenario } = useSlideout();

    const { data: fetchedDeal, isLoading } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const {
        data: { items: boeScenarios } = {},
        isLoading: areScenariosLoading,
    } = useGetBackOfEnvelopeScenarios({ orgId: selectedOrganizationId!, dealId: Number(dealId) });

    useEffect(() => {
        const activeTabInfo = locallyStoredActiveTabName[dealId as string];
        setLocallyStoredActiveTabName(prev => ({ ...prev, [dealId as string]: { ...activeTabInfo, tab: activeTabName } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTabName, dealId]);

    useEffect(() => {
        const tabSlug = TAB_TO_SLUG[activeTabName];
        const canonicalPath = generatePath(URLS.DEAL_DASHBOARD.HOME, { dealId, tab: tabSlug });

        if (window.location.pathname !== canonicalPath) {
            navigate(canonicalPath, { replace: true });
        }
    }, [activeTabName, dealId, navigate]);

    // Ensure selectedScenario is always set, fallback to first scenario if undefined
    const initialScenario = selectedScenario
        || (
            boeScenarios && boeScenarios.length > 0
                ? { label: boeScenarios[0].name, value: boeScenarios[0].id }
                : undefined
        );

    const handleScenarioChange = useCallback((scenarioId: number) => {
        const activeTabInfo = locallyStoredActiveTabName[dealId as string];
        setLocallyStoredActiveTabName(prev => ({
            ...prev,
            [dealId as string]: { ...activeTabInfo, tab: activeTabName, scenarioId: scenarioId.toString() },
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTabName, dealId]);

    const handlePdfExport = async (pdfUrl: string, fileName: string) => {
        if (pdfUrl) {
            await downloadFileFromUrl(pdfUrl, fileName, 'pdf', true);
        } else {
            console.error('Failed to export report PDF: No URL returned');
        }
    };

    const exportDealOverviewPdf = async (deal?: Deal) => {
        if (deal) {
            const response = await getDealOverviewPdf(deal.organizationId, deal.id);
            const pdfUrl = response.url;
            handlePdfExport(pdfUrl, `${deal.dealName}-deal-overview`);
        }
    };

    const exportScenarioDashboardPdf = async (deal?: Deal) => {
        const scenarioId = selectedScenarioId || boeScenarios?.[0]?.id;
        if (deal && scenarioId) {
            const response = await getScenarioDashboardPdf(deal.organizationId, Number(scenarioId));
            const pdfUrl = response.url;
            handlePdfExport(pdfUrl, `${deal.dealName}-scenario-dashboard`);
        }
    };

    const isEmptySavedScenarios = !boeScenarios || boeScenarios?.length === 0;

    const getExportOptionsHeapClassName = (option: ExportOptions) => {
        const heapClassNames: Record<ExportOptions, string> = {
            [ExportOptions.DEAL_OVERVIEW]: 'heap-deal-dashboard-export-deal-overview',
            [ExportOptions.SCENARIO_DASHBOARD]: 'heap-deal-dashboard-export-back-of-envelope',
        };
        return heapClassNames[option] || '';
    };

    const exportOptions = Object.values(ExportOptions)
        .filter(option => !(option === ExportOptions.SCENARIO_DASHBOARD && isEmptySavedScenarios))
        .map(option => ({
            value: option,
            label: option,
            className: getExportOptionsHeapClassName(option),
        }));

    const handleSelectExportOption = async (option: IOptionAnyValue) => {
        setIsDownloading(true);
        const toastId = showToast('Downloading PDF...', ToastType.Success, { autoClose: false });
        try {
            switch (option.value) {
                case ExportOptions.SCENARIO_DASHBOARD:
                    await exportScenarioDashboardPdf(fetchedDeal);
                    break;
                case ExportOptions.DEAL_OVERVIEW:
                    await exportDealOverviewPdf(fetchedDeal);
                    break;
                default:
                    break;
            }
            showToast(
                'PDF downloaded successfully!',
                ToastType.Success,
                { autoClose: 3000 },
                toastId,
            );
        } catch (error) {
            console.error('Failed to export report PDF:', error);
            showToast(
                'Failed to download PDF',
                ToastType.Error,
                { autoClose: 3000 },
                toastId,
            );
        } finally {
            setIsDownloading(false);
        }
    };

    const toggleOpenProformaModal = () => setOpenCreateProFormaModal(!openCreateProFormaModal);

    const getHeapClassName = (tabName: string) => {
        const heapClassNames: Record<string, string> = {
            [DEAL_OVERVIEW]: 'heap-deal-dashboard-overview-tab',
            [BACK_OF_ENVELOPE]: 'heap-deal-dashboard-back-of-envelope-tab',
            [DEAL_ACTIVITY]: 'heap-deal-dashboard-activity-tab',
        };
        return heapClassNames[tabName] || '';
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (!fetchedDeal) {
        return <div>No deal selected</div>;
    }

    const BoEfeatureIsAccessible = isFeatureAccessibleToUser([FEATURE_IDS.BACK_OF_ENVELOPE], AccessLevels.READ_ACCESS);

    if (!BoEfeatureIsAccessible) {
        visibleDealOverviewTabs = visibleDealOverviewTabs.filter(tab => tab !== BACK_OF_ENVELOPE);
    }

    return (
        <div className="DealDashboard">
            <PageTitle
                title={fetchedDeal.dealName}
                dealName={fetchedDeal.dealName}
                pageMetaDataTitle="Deal dashboard"
                subtitle={[fetchedDeal.address, fetchedDeal.city, fetchedDeal.state, fetchedDeal.zipCode]
                    .filter(val => val != null && val !== '')
                    .join(' ')}
                beta
                betaCustomText="Beta"
                displayBreadcrumb
                boldTitle
            >
                <div className="d-flex">
                    <NSDropdown
                        containerClassName="mr-2"
                        options={exportOptions}
                        customSelectedOptionText="Export"
                        handleSelectOption={handleSelectExportOption}
                        disabled={!fetchedDeal || isDownloading}
                        menuRight
                    />
                    <NSButton
                        color="primary"
                        id="create-pro-forma"
                        className="heap-deal-dashboard-create-pro-forma"
                        outline={false}
                        callback={toggleOpenProformaModal}
                        text="Create pro forma"
                    />
                </div>
            </PageTitle>

            <NSNavTabs
                containerClassName="d-flex mt-2 DealDashboard__separator"
                navItems={visibleDealOverviewTabs.map((navName, index) => ({
                    callback: () => navigate(generatePath(URLS.DEAL_DASHBOARD.HOME, { dealId, tab: TAB_TO_SLUG[navName] })),
                    name: navName,
                    isActive: activeTabName === navName,
                    id: `deal-overview-${index}`,
                    className: `text-dark text-nowrap px-4 mr-2 ${getHeapClassName(navName)}`,
                }))}
            />

            <TabContent activeTab={activeTabName} className="DealDashboard__tab-content d-flex w-100">
                <RenderIf isTrue={activeTabName === DEAL_OVERVIEW}>
                    <TabPane className="d-flex flex-column w-100" tabId={DEAL_OVERVIEW}>
                        <Overview deal={fetchedDeal} />
                    </TabPane>
                </RenderIf>
                <RenderIf isTrue={activeTabName === BACK_OF_ENVELOPE}>
                    <TabPane className="d-flex flex-column w-100" tabId={BACK_OF_ENVELOPE}>
                        <BackOfEnvelope
                            orgId={selectedOrganizationId!}
                            dealId={Number(dealId)}
                            boeScenarios={boeScenarios}
                            handleScenarioChange={handleScenarioChange}
                            defaultScenarioId={defaultScenarioId ? Number(defaultScenarioId) : undefined}
                        />
                    </TabPane>
                </RenderIf>
                <RenderIf isTrue={activeTabName === DEAL_ACTIVITY}>
                    <TabPane className="d-flex flex-column w-100 pt-0" tabId={DEAL_ACTIVITY}>
                        <Activity
                            dealId={Number(dealId)}
                            orgId={selectedOrganizationId!}
                        />
                    </TabPane>
                </RenderIf>
            </TabContent>
            <RenderIf isTrue={openCreateProFormaModal && !areScenariosLoading}>
                <CreateProFormaModal
                    toggle={toggleOpenProformaModal}
                    isOpen={openCreateProFormaModal}
                    initialScenario={initialScenario}
                    dealAssetTypeCorrelationId={fetchedDeal?.assetType?.correlationId || ''}
                />
            </RenderIf>
        </div>
    );
};

export default DealDashboard;
